import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Select } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';

import { EstablishmentStore } from '../../features/establishment';

@Component({
  selector: 'app-tenant-picker',
  standalone: true,
  imports: [FormsModule, Select, Skeleton],
  template: `
    @if (isLoading()) {
      <p-skeleton width="180px" height="38px" styleClass="rounded-md" />
    } @else if (hasMultipleEstablishments()) {
      <p-select
        [options]="establishmentOptions()"
        [(ngModel)]="selectedEstablishmentId"
        optionLabel="label"
        optionValue="value"
        (onChange)="onEstablishmentChange($event)"
        styleClass="w-full md:w-48"
        [style]="{ minWidth: '180px' }"
      >
        <ng-template pTemplate="selectedItem">
          @if (currentEstablishment()) {
            <div class="flex items-center gap-2">
              <i class="pi pi-building text-primary-600"></i>
              <span class="truncate max-w-[140px]">{{
                currentEstablishment()?.establishmentName
              }}</span>
            </div>
          }
        </ng-template>
        <ng-template pTemplate="item" let-option>
          <div class="flex items-center gap-2">
            <i class="pi pi-building"></i>
            <span>{{ option.label }}</span>
            @if (option.isDefault) {
              <span class="text-xs text-primary-600 ml-auto">(default)</span>
            }
          </div>
        </ng-template>
      </p-select>
    } @else if (currentEstablishment()) {
      <div class="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-md">
        <i class="pi pi-building text-primary-600"></i>
        <span class="text-sm font-medium text-gray-700 dark:text-gray-200 truncate max-w-[160px]">
          {{ currentEstablishment()?.establishmentName }}
        </span>
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class TenantPickerComponent {
  private establishmentStore = inject(EstablishmentStore);
  private router = inject(Router);

  readonly currentEstablishment = this.establishmentStore.currentEstablishment;
  readonly establishments = this.establishmentStore.establishments;
  readonly isLoading = this.establishmentStore.isLoading;
  readonly hasMultipleEstablishments = this.establishmentStore.hasMultipleEstablishments;

  readonly establishmentOptions = computed(() =>
    this.establishments().map((e) => ({
      label: e.establishmentName ?? 'Unknown',
      value: e.establishmentId,
      isDefault: e.isDefault,
    })),
  );

  get selectedEstablishmentId(): number | null {
    return this.establishmentStore.currentEstablishmentId();
  }

  set selectedEstablishmentId(value: number | null) {
    // ngModel setter fires on selection — actual switching handled by onEstablishmentChange
  }

  onEstablishmentChange(event: { value: number }): void {
    this.establishmentStore.switchEstablishment(event.value);
    // Navigate to dashboard to trigger onboardingGuard re-evaluation for the new establishment
    void this.router.navigate(['/dashboard']);
  }
}
